import WebApp from '@twa-dev/sdk'
import { FormEvent, ReactNode, useEffect, useState } from 'react'
import { Loader2, LogIn } from 'lucide-react'
import { useLanguage } from '../hooks/useLanguage'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export function WebAuthGate({ children }: { children: ReactNode }) {
  const { lang, setLang, t } = useLanguage()
  const inTelegram = Boolean(WebApp.initData)
  const [checking, setChecking] = useState(!inTelegram)
  const [authenticated, setAuthenticated] = useState(inTelegram)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (inTelegram) return
    void fetch(`${API_BASE_URL}/api/auth/me`, { credentials: 'include' })
      .then(async response => {
        if (response.ok) {
          const account = await response.json()
          sessionStorage.setItem('web3_miniapp_user_id', String(account.user_id))
          setAuthenticated(true)
        } else setAuthenticated(false)
      })
      .catch(() => setAuthenticated(false))
      .finally(() => setChecking(false))
  }, [inTelegram])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true); setError('')
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login-or-create`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!response.ok) {
        const body = await response.json().catch(() => ({ detail: '登录失败，请稍后重试' }))
        throw new Error(body.detail || '登录失败，请稍后重试')
      }
      const account = await response.json()
      sessionStorage.setItem('web3_miniapp_user_id', String(account.user_id))
      setAuthenticated(true)
      setPassword('')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '登录失败，请稍后重试')
    } finally {
      setSubmitting(false)
    }
  }

  if (checking) return <Shell><Loader2 className="h-7 w-7 animate-spin text-blue-600" /><p>{t('正在检查登录状态…', 'Checking sign-in status…')}</p></Shell>
  if (authenticated) return <>{children}</>
  return <Shell>
    <LogIn className="h-9 w-9 text-blue-600" />
    <button type="button" onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')} className="absolute right-5 top-5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600">{lang === 'zh' ? 'English' : '中文'}</button>
    <h1 className="text-xl font-bold text-gray-900">{t('登录以继续', 'Sign in to continue')}</h1>
    <p className="max-w-sm text-center text-sm text-gray-500">{t('无需注册。首次使用此邮箱登录时，您输入的密码会成为此账号的永久密码。', 'No registration is needed. Your password on first sign-in becomes this account’s permanent password.')}</p>
    <p className="max-w-sm text-center text-xs text-amber-700">{t('当前不支持密码找回，请妥善保存邮箱和密码。', 'Password recovery is not available. Keep your email and password safe.')}</p>
    <form onSubmit={submit} className="mt-2 w-full max-w-sm space-y-3">
      <input aria-label={t('邮箱', 'Email')} type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder={t('邮箱地址', 'Email address')} className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
      <input aria-label={t('密码', 'Password')} type="password" required minLength={10} value={password} onChange={event => setPassword(event.target.value)} placeholder={t('密码（至少 10 位）', 'Password (at least 10 characters)')} className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500" />
      {error && <p role="alert" className="text-center text-sm text-red-600">{error}</p>}
      <button disabled={submitting} type="submit" className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white disabled:opacity-60">
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{submitting ? t('正在登录…', 'Signing in…') : t('登录 / 首次创建账号', 'Sign in / Create account')}
      </button>
    </form>
  </Shell>
}

function Shell({ children }: { children: ReactNode }) {
  return <main className="relative flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-gray-700">{children}</main>
}
